import type { ReferenceRole, RoledReference } from './image'

// A reference on a proposal card also says which picture it is and the role it plays.
export type ChatImage = {
  readonly key: string
  readonly url?: string | null
  readonly id?: string
  readonly role?: ReferenceRole
  readonly name?: string | null
}

export type DesignLook = {
  readonly template: string
  readonly colours: readonly { readonly hex: string; readonly role: string | null }[]
  readonly fonts: readonly string[]
  readonly photo: boolean
}

// The page names a kind's title and action; the tool fills the rest. A card with options waits for one to be picked.
export type ChatCard = {
  readonly kind: string
  readonly title?: string
  readonly markdown?: string
  readonly images?: readonly ChatImage[]
  readonly options?: readonly string[]
  readonly notes?: readonly string[]
  readonly pick?: number
  readonly part?: string
  readonly ratio?: string
  readonly prompt?: string
  readonly line?: string
  readonly look?: DesignLook
  readonly steps?: readonly { readonly name: string | null; readonly change: string }[]
  readonly versions?: readonly { readonly images: readonly ChatImage[] }[]
  readonly questions?: readonly ChatQuestion[]
}

// One question of a questions card: its options, the one the agent recommends, and whether several may be picked.
export type ChatQuestion = { readonly ask: string; readonly options: readonly string[]; readonly pick?: number; readonly multi?: boolean }

// `picked` counts from 1, as the options are numbered for the user and the model; `references` are a proposal's as approved.
export type ChatAnswer = {
  readonly approved: boolean
  readonly message?: string
  readonly picked?: number
  readonly references?: readonly RoledReference[]
}

export const MEMORY_TOOL = { remember: 'remember', forget: 'forget' } as const

export const secondsBetween = (start: number, end: number) => Math.max(1, Math.round((end - start) / 1000))

export type ChatPart =
  | { readonly kind: 'text'; readonly id: string; readonly text: string }
  | { readonly kind: 'reasoning'; readonly id: string; readonly text: string; readonly seconds?: number }
  | {
      readonly kind: 'tool'
      readonly id: string
      readonly name: string
      readonly input: unknown
      readonly output?: unknown
      readonly error?: string
      readonly done: boolean
    }
  | {
      readonly kind: 'card'
      readonly id: string
      readonly name: string
      readonly card: ChatCard
      readonly answer: ChatAnswer | null
      readonly interruptId: string | null
      readonly images?: readonly ChatImage[]
      readonly references?: readonly ChatImage[]
      readonly assetId?: string
      readonly failed?: string
    }

export type ChatTurn =
  | { readonly id: string; readonly role: 'user'; readonly text: string }
  | {
      readonly id: string
      readonly role: 'agent'
      readonly parts: readonly ChatPart[]
      readonly seconds?: number
      readonly stopped?: boolean
      readonly suggestions?: readonly string[]
    }

export type ChatThread = { readonly turns: readonly ChatTurn[]; readonly waiting: string | null }

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

// A tool row shows words and pictures: its input's plain values, its output's images and what it marks `shown`, never the documents it carried.
export const shownInput = (input: unknown) =>
  isRecord(input) ? Object.fromEntries(Object.entries(input).filter(([, value]) => typeof value !== 'object' || value === null)) : {}

// A card's stored answer as the thread shows it.
export const answerOf = (output: Record<string, unknown>) => ({
  approved: output.approved === true,
  ...(typeof output.message === 'string' ? { message: output.message } : {}),
  ...(typeof output.picked === 'number' ? { picked: output.picked } : {}),
  ...(Array.isArray(output.references) ? { references: (output.references as RoledReference[]).map(({ id, role }) => ({ id, role })) } : {})
})

// What an answered card made: the pictures it returned, or why it could not.
export const madeBy = (output: Record<string, unknown>) => ({
  ...(Array.isArray(output.images) ? { images: output.images as ChatImage[] } : {}),
  ...(Array.isArray(output.references) ? { references: output.references as ChatImage[] } : {}),
  ...(typeof output.assetId === 'string' ? { assetId: output.assetId } : {}),
  ...(typeof output.failed === 'string' ? { failed: output.failed } : {})
})

// A canvas edit keeps the name of the layer it changed, and a refused one its reason: the thread shows what landed, and what did not.
// A row that names what it showed keeps it beside its pictures.
export const shownOutput = (output: unknown) => {
  if (!isRecord(output)) return undefined
  const shown = isRecord(output.shown) ? { shown: output.shown } : {}
  if (Array.isArray(output.images)) return { images: output.images, ...shown }
  if (isRecord(output.shown)) return shown
  if (output.ok === false && typeof output.error === 'string') return { refused: output.error }
  return isRecord(output.layer) && typeof output.layer.label === 'string' ? { label: output.layer.label } : undefined
}
